import React, { useState } from 'react';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from "recharts";
import {
  Activity,
  TrendingUp,
  Clock,
  DollarSign,
  LineChart as LineChartIcon,
  BarChart2,
  PieChart as PieChartIcon
} from 'lucide-react';

const ChartsComponent = ({
  salesTrend,
  salesByCategory,
  hourlyTraffic,
  monthlyRevenue,
  COLORS,
  dateRange
}) => {
  const [salesChartType, setSalesChartType] = useState('area');
  const [hourlyChartType, setHourlyChartType] = useState('bar');
  const [revenueChartType, setRevenueChartType] = useState('line');

  const renderChart = (type, data, dataKeys) => {
    switch (type) {
      case 'line':
        return (
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(226, 232, 240, 0.3)" />
            <XAxis dataKey={dataKeys.x} stroke="#94A3B8" />
            <YAxis stroke="#94A3B8" />
            <Tooltip
              contentStyle={{
                backgroundColor: "#1E293B",
                border: "none",
                borderRadius: "8px",
                boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
                color: "#F1F5F9",
              }}
            />
            <Legend />
            {dataKeys.y.map((key, index) => (
              <Line
                key={key}
                type="monotone"
                dataKey={key}
                stroke={COLORS[index % COLORS.length]}
                strokeWidth={2}
                dot={{ fill: COLORS[index % COLORS.length], strokeWidth: 2, r: 6 }}
                activeDot={{ r: 8, stroke: COLORS[index % COLORS.length], strokeWidth: 2 }}
              />
            ))}
          </LineChart>
        );
      case 'bar':
        return (
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(226, 232, 240, 0.3)" />
            <XAxis dataKey={dataKeys.x} stroke="#94A3B8" />
            <YAxis stroke="#94A3B8" />
            <Tooltip
              contentStyle={{
                backgroundColor: "#1E293B",
                border: "none",
                borderRadius: "8px",
                boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
                color: "#F1F5F9",
              }}
            />
            <Legend />
            {dataKeys.y.map((key, index) => (
              <Bar
                key={key}
                dataKey={key}
                fill={COLORS[index % COLORS.length]}
                radius={[4, 4, 0, 0]}
              />
            ))}
          </BarChart>
        );
      case 'area':
        return (
          <AreaChart data={data}>
            <defs>
              <linearGradient id="colorVentas" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="colorIngresos" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#10B981" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(226, 232, 240, 0.3)" />
            <XAxis dataKey={dataKeys.x} stroke="#94A3B8" />
            <YAxis stroke="#94A3B8" />
            <Tooltip
              contentStyle={{
                backgroundColor: "#1E293B",
                border: "none",
                borderRadius: "8px",
                boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
                color: "#F1F5F9",
              }}
            />
            <Legend />
            <Area type="monotone" dataKey="ventas" stackId="1" stroke="#3B82F6" fill="url(#colorVentas)" strokeWidth={2} />
            <Area type="monotone" dataKey="ingresos" stackId="2" stroke="#10B981" fill="url(#colorIngresos)" strokeWidth={2} />
          </AreaChart>
        );
      default:
        return null;
    }
  };

  return (
    <>
      {/* Fila 1 de gráficos */}
      <div className="chart-row">
        {/* Tendencia de Ventas */}
        <div className="chart-panel glass-panel">
          <div className="panel-header">
            <div className="panel-icon-wrapper">
              <TrendingUp className="panel-icon" />
            </div>
            <h3>Tendencia de Ventas ({dateRange})</h3>
            <div className="chart-type-filter">
              <button
                className={`filter-icon-button ${salesChartType === 'area' ? 'active' : ''}`}
                onClick={() => setSalesChartType('area')}
              >
                <TrendingUp size={16} />
              </button>
              <button
                className={`filter-icon-button ${salesChartType === 'line' ? 'active' : ''}`}
                onClick={() => setSalesChartType('line')}
              >
                <LineChartIcon size={16} />
              </button>
              <button
                className={`filter-icon-button ${salesChartType === 'bar' ? 'active' : ''}`}
                onClick={() => setSalesChartType('bar')}
              >
                <BarChart2 size={16} />
              </button>
            </div>
          </div>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={300}>
              {renderChart(salesChartType, salesTrend, { x: 'name', y: ['ventas', 'ingresos'] })}
            </ResponsiveContainer>
          </div>
        </div>

        {/* Tráfico por Horas */}
        <div className="chart-panel glass-panel">
          <div className="panel-header">
            <div className="panel-icon-wrapper">
              <Clock className="panel-icon" />
            </div>
            <h3>Tráfico por Horas</h3>
            <div className="chart-type-filter">
              <button
                className={`filter-icon-button ${hourlyChartType === 'bar' ? 'active' : ''}`}
                onClick={() => setHourlyChartType('bar')}
              >
                <BarChart2 size={16} />
              </button>
              <button
                className={`filter-icon-button ${hourlyChartType === 'line' ? 'active' : ''}`}
                onClick={() => setHourlyChartType('line')}
              >
                <LineChartIcon size={16} />
              </button>
            </div>
          </div>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={300}>
              {renderChart(hourlyChartType, hourlyTraffic, { x: 'hora', y: ['clientes', 'ventas'] })}
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Fila 2 de gráficos */}
      <div className="chart-row">
        {/* Ingresos Mensuales */}
        <div className="chart-panel glass-panel">
          <div className="panel-header">
            <div className="panel-icon-wrapper">
              <DollarSign className="panel-icon" />
            </div>
            <h3>Ingresos Mensuales vs Objetivo</h3>
            <div className="chart-type-filter">
              <button
                className={`filter-icon-button ${revenueChartType === 'line' ? 'active' : ''}`}
                onClick={() => setRevenueChartType('line')}
              >
                <LineChartIcon size={16} />
              </button>
              <button
                className={`filter-icon-button ${revenueChartType === 'bar' ? 'active' : ''}`}
                onClick={() => setRevenueChartType('bar')}
              >
                <BarChart2 size={16} />
              </button>
            </div>
          </div>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={300}>
              {renderChart(revenueChartType, monthlyRevenue, { x: 'mes', y: ['ingresos', 'objetivo'] })}
            </ResponsiveContainer>
          </div>
        </div>

        {/* Ventas por Categoría (Nuevo gráfico circular) */}
        <div className="chart-panel glass-panel">
          <div className="panel-header">
            <div className="panel-icon-wrapper">
              <PieChartIcon className="panel-icon" />
            </div>
            <h3>Ventas por Categoría</h3>
          </div>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#1E293B",
                    border: "none",
                    borderRadius: "8px",
                    boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
                    color: "#F1F5F9",
                  }}
                />
                <Legend />
                <Pie
                  data={salesByCategory}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  fill="#8884d8"
                  label
                >
                  {salesByCategory.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Fila de Resumen Ejecutivo */}
      <div className="chart-row">
        <div className="chart-panel full-width glass-panel">
          <div className="panel-header">
            <div className="panel-icon-wrapper">
              <Activity className="panel-icon" />
            </div>
            <h3>Resumen Ejecutivo</h3>
          </div>
          <div className="chart-container">
            <div className="executive-summary">
              <div className="summary-item">
                <div className="summary-icon success">
                  <TrendingUp size={24} />
                </div>
                <div className="summary-content">
                  <h4>Crecimiento Mensual</h4>
                  <div className="summary-value">+23.5%</div>
                  <p>Comparado con el mes anterior</p>
                </div>
              </div>
              <div className="summary-item">
                <div className="summary-icon info">
                  <DollarSign size={24} />
                </div>
                <div className="summary-content">
                  <h4>Ticket Promedio</h4>
                  <div className="summary-value">S/34.50</div>
                  <p>+5.2% vs mes anterior</p>
                </div>
              </div>
              <div className="summary-item">
                <div className="summary-icon warning">
                  <Clock size={24} />
                </div>
                <div className="summary-content">
                  <h4>Hora Pico</h4>
                  <div className="summary-value">18:00</div>
                  <p>35% del tráfico diario</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .chart-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
          margin-bottom: 20px;
        }

        .chart-panel {
          display: flex;
          flex-direction: column;
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .chart-panel.full-width {
            grid-column: 1 / -1;
        }

        .panel-header {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 16px 20px;
          border-bottom: 1px solid #e5e7eb;
          justify-content: space-between;
        }

        .panel-header h3 {
          margin: 0;
          font-size: 16px;
          font-weight: 600;
          color: #1f2937;
          flex: 1;
        }

        .panel-icon-wrapper {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          background: #3b82f6;
          border-radius: 8px;
          flex-shrink: 0;
        }

        .panel-icon {
          width: 18px;
          height: 18px;
          color: white;
        }

        .chart-container {
          padding: 20px;
          flex: 1;
        }

        /* Chart Type Filter */
        .chart-type-filter {
          display: flex;
          gap: 8px;
        }

        .filter-icon-button {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 6px;
          border-radius: 6px;
          background: transparent;
          border: 1px solid transparent;
          color: #6b7280;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .filter-icon-button:hover {
          background: #e5e7eb;
        }

        .filter-icon-button.active {
          background: #3b82f6;
          color: white;
          border-color: #3b82f6;
        }

        .executive-summary {
          display: flex;
          flex-direction: row;
          justify-content: space-around;
          align-items: center;
          gap: 20px;
        }

        .summary-item {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 16px;
          background: #f9fafb;
          border-radius: 8px;
          border: 1px solid #e5e7eb;
          flex: 1;
        }

        .summary-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .summary-icon.success {
          background: rgba(16, 185, 129, 0.1);
          color: #10b981;
        }

        .summary-icon.info {
          background: rgba(59, 130, 246, 0.1);
          color: #3b82f6;
        }

        .summary-icon.warning {
          background: rgba(245, 158, 11, 0.1);
          color: #f59e0b;
        }

        .summary-content {
          flex: 1;
        }

        .summary-content h4 {
          margin: 0 0 4px 0;
          font-size: 14px;
          font-weight: 500;
          color: #6b7280;
        }

        .summary-value {
          font-size: 24px;
          font-weight: 700;
          color: #1f2937;
          margin-bottom: 4px;
        }

        .summary-content p {
          margin: 0;
          font-size: 12px;
          color: #9ca3af;
        }

        @media (max-width: 1280px) {
          .chart-row {
            grid-template-columns: 1fr;
          }
          .executive-summary {
            flex-direction: column;
            align-items: stretch;
          }
        }
      `}</style>
    </>
  );
};

export default ChartsComponent;